import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Spinner from '../../../components/common/Spinner';
import { useAuth } from '../../../context/AuthContext';
import { exchangeKakaoCode, processSocialSignInResult } from '../../../services/authService';
import { isRealtorAutoConnectRedirect, normalizeSignupRedirectUrl } from '../../../utils/signupRedirect';

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #F8F9FA;
`;

const Label = styled.p`
  margin-top: 16px;
  font-size: 14px;
  color: #4B5563;
`;

const ErrorBox = styled.div`
  max-width: 360px;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid #FECACA;
  background: #FEF2F2;
  color: #B91C1C;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  white-space: pre-wrap;
`;

const RetryBtn = styled.button`
  margin-top: 16px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: #4A6CF7;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const KakaoCallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshSession } = useAuth();
  const [error, setError] = useState(null);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    (async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      const errQ = params.get('error');
      const errDesc = params.get('error_description');

      if (errQ) {
        setError(errDesc || errQ);
        return;
      }
      if (!code) {
        setError('카카오 인증 코드가 없습니다.');
        return;
      }
      let savedState = '';
      try { savedState = sessionStorage.getItem('kakao.oauth.state') || ''; } catch {}
      if (savedState && state && savedState !== state) {
        setError('인증 요청이 변조되었을 수 있습니다. 다시 시도해주세요.');
        return;
      }
      try { sessionStorage.removeItem('kakao.oauth.state'); } catch {}
      let savedReturnTo = '';
      try {
        savedReturnTo = normalizeSignupRedirectUrl(sessionStorage.getItem('kakao.oauth.returnTo') || '');
        sessionStorage.removeItem('kakao.oauth.returnTo');
      } catch {}

      try {
        const redirectUri = `${window.location.origin}/auth/kakao/callback`;
        const signed = await exchangeKakaoCode(code, redirectUri);
        const result = await processSocialSignInResult(signed);

        if (result.kind === 'home') {
          await refreshSession();
          const canReturnToRealtorAuto =
            isRealtorAutoConnectRedirect(savedReturnTo) && ['admin', 'staff', 'realtor'].includes(result.user?.role);
          if (savedReturnTo && (result.user?.role === 'realtor' || canReturnToRealtorAuto)) {
            window.location.assign(savedReturnTo);
            return;
          }
          navigate('/', { replace: true });
          return;
        }
        if (result.kind === 'link-phone') {
          navigate('/social/link-phone', {
            replace: true,
            state: { ...result.state, returnTo: savedReturnTo },
          });
          return;
        }
        // alert (pending / suspended)
        const msg = `${result.alert?.title || ''}\n${result.alert?.message || ''}`.trim();
        setError(msg);
      } catch (e) {
        setError(e?.message || '카카오 로그인에 실패했습니다.');
      }
    })();
  }, [location.search, navigate, refreshSession]);

  return (
    <Wrap>
      {error ? (
        <>
          <ErrorBox>{error}</ErrorBox>
          <RetryBtn onClick={() => navigate('/login', { replace: true })}>로그인으로 돌아가기</RetryBtn>
        </>
      ) : (
        <>
          <Spinner />
          <Label>카카오 로그인 처리 중...</Label>
        </>
      )}
    </Wrap>
  );
};

export default KakaoCallbackPage;
